import { Fragment } from 'react';
import { ScaleLinear } from 'd3-scale';

import { AreaClosed, Bar, LinePath } from '@visx/shape';
import { curveMonotoneX } from '@visx/curve';
import { Group } from '@visx/group';
import { LinearGradient } from '@visx/gradient';

import { hasNoNeighbors } from '../timeseries-chart/utils';

import { CategoryScale, CategoryValue } from './types';

type AreaSeriesProps = {
  data: { categoryValue: CategoryValue; value: number | null }[];
  fillColor: string;
  strokeColor: string;
  opacity?: number;
  valueScale: ScaleLinear<number, number>;
  categoryScale: CategoryScale;
};

export const AreaSeries = (props: AreaSeriesProps) => {
  const { data, fillColor, strokeColor, opacity = 1, valueScale, categoryScale } = props;

  return (
    <Group>
      <LinearGradient
        id={`gradient-${fillColor}`}
        from={fillColor}
        to={fillColor}
        fromOpacity={opacity}
        toOpacity={0.3}
      />
      <AreaClosed
        data={data}
        x={({ categoryValue }) =>
          (categoryScale(categoryValue.formatted) ?? 0) + categoryScale.bandwidth() / 2
        }
        y0={({ value }) => valueScale(value ?? 0)}
        y1={() => valueScale(0)}
        defined={({ value }) => value !== null}
        yScale={valueScale}
        fill={`url(#gradient-${fillColor})`}
        fillOpacity={opacity}
        curve={curveMonotoneX}
      />
      <LinePath
        data={data}
        x={({ categoryValue }) =>
          (categoryScale(categoryValue.formatted) ?? 0) + categoryScale.bandwidth() / 2
        }
        y={({ value }) => valueScale(value ?? 0)}
        defined={({ value }) => value !== null}
        stroke={strokeColor}
        strokeOpacity={opacity}
        curve={curveMonotoneX}
      />
      {data
        .filter((_, i) => hasNoNeighbors(data, i))
        .map(({ categoryValue, value }) => {
          if (value === null) {
            return null;
          }
          return (
            <Fragment key={`dot-${categoryValue.formatted}`}>
              <Bar
                x={
                  (categoryScale(categoryValue.formatted) ?? 0) + categoryScale.bandwidth() / 2 - 1
                }
                y={valueScale(value)}
                width={2}
                height={valueScale(0) - valueScale(value)}
                fill={fillColor}
              />
              <Bar
                x={
                  (categoryScale(categoryValue.formatted) ?? 0) + categoryScale.bandwidth() / 2 - 1
                }
                y={valueScale(value) - 2}
                width={2}
                height={2}
                fill={strokeColor}
              />
            </Fragment>
          );
        })}
    </Group>
  );
};
